import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useEffect } from 'react';

export function useUnreadNotifications(siteId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: unreadCount = 0, isLoading } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: async () => {
      if (!user?.id) return 0;
      
      const { data, error } = await supabase
        .from('notifications')
        .select('id, data')
        .eq('user_id', user.id)
        .eq('read', false);
      
      if (error) throw error;
      if (!data) return 0;
      
      // Filter by site_id if provided
      if (siteId) {
        const filtered = data.filter(n => {
          const notifSiteId = (n.data as any)?.site_id || (n.data as any)?.siteId;
          return notifSiteId === siteId;
        });
        return filtered.length;
      }
      
      return data.length;
    },
    enabled: !!user?.id,
    staleTime: 30 * 1000,
    gcTime: 10 * 60 * 1000,         // Cache 10 min (optimisé)
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  // Subscribe to realtime changes
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`notifications-unread-count-${siteId || 'all'}`)
      .on(
        'postgres_changes',
        {
          event: '*', // Listen to INSERT, UPDATE, DELETE
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          // Invalidate the query to refetch the count
          queryClient.invalidateQueries({ queryKey: ['notifications', 'unread-count'] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, siteId, queryClient]);

  return {
    unreadCount,
    isLoading
  };
}
